import { useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/rootReducer';
import { isRealtimeEnabled } from '../../../config/config';
import { IAsyncOperationState, ITopicTarget } from '../../../api/realtime/types';
import {
  finishedOperationsHandled,
  followAsyncOperationsTopic,
  loadRecallOperationsSnapshot,
  resyncAsyncOperationsTopic,
  unfollowAsyncOperationsTopic,
} from '../../../store/reducers/asyncOperations/actions';
import {
  finishedTopicOperationsSelector,
  topicStatusSelector,
} from '../../../store/reducers/asyncOperations/selectors';
import { loadRecalls } from '../../../store/reducers/recall/actions';
import { loadMakesAll } from '../../../store/reducers/vehicleDetails/actions';

const RESYNC_INTERVAL_MS = 60_000;
const POLL_INTERVAL_MS = 5_000;
const COMPLETED_VISIBLE_MS = 5_000;

/**
 * Follows the recall operations of the service center while the recalls page is open: live over
 * the hub, through the REST snapshot while the hub is down. Reloads the recalls quietly when an
 * operation completes or the data changed under the page.
 * It selects plain values only, so a progress message re-renders the banner, which selects the
 * running operation itself, and not the whole page.
 */
export const useRecallOperations = (serviceCenterId?: number) => {
  const isEnabled = useMemo(() => isRealtimeEnabled(), []);
  const dispatch = useDispatch();
  const target = useMemo<ITopicTarget | null>(
    () => (isEnabled && serviceCenterId ? { serviceCenterId, topic: 'Recalls' } : null),
    [isEnabled, serviceCenterId]
  );
  const { isOperationRunning, isAwaiting, connectionStatus, subscriptionError, dataChangedAt } =
    useSelector((state: RootState) => topicStatusSelector(state, target), shallowEqual);
  const finishedOperations = useSelector(
    (state: RootState) => finishedTopicOperationsSelector(state, target),
    shallowEqual
  );
  const [finishedOperation, setFinishedOperation] = useState<IAsyncOperationState | null>(null);
  // the follow on entering the page drops an earlier visit's results before they are handled
  const isFollowingRef = useRef(false);
  // the first time the page is live after entering it, the table has just loaded
  const isFirstLiveRef = useRef(true);

  // an operation runs, or this tab waits for one it submitted and has not seen yet
  const isWaiting = isOperationRunning || isAwaiting;
  const isLive = connectionStatus === 'Connected' && !subscriptionError;
  const lastDataChangedAtRef = useRef(dataChangedAt);

  const reloadRecalls = (scId: number) => dispatch(loadRecalls(scId, undefined, { silent: true }));

  // subscribe on entering the page, unsubscribe on leaving it
  useEffect(() => {
    if (!target) return undefined;
    dispatch(followAsyncOperationsTopic(target));
    if (connectionStatus !== 'Connected') {
      // the hub snapshot comes once it connects; until then the page renders from REST
      dispatch(loadRecallOperationsSnapshot(target.serviceCenterId));
    }
    lastDataChangedAtRef.current = dataChangedAt;
    isFirstLiveRef.current = true;
    return () => {
      dispatch(unfollowAsyncOperationsTopic(target));
      setFinishedOperation(null);
    };
  }, [target]);

  // the hub refused to follow the page: fall back to the REST snapshot
  useEffect(() => {
    if (target && subscriptionError) dispatch(loadRecallOperationsSnapshot(target.serviceCenterId));
  }, [subscriptionError]);

  // data change hints sent while the hub was down are lost: reload once the page is live again
  useEffect(() => {
    if (!target || !isLive) return;
    if (!isFirstLiveRef.current) reloadRecalls(target.serviceCenterId);
    isFirstLiveRef.current = false;
  }, [target, isLive]);

  // while live, heal a missed update every minute; while the hub is down, poll while waiting
  useEffect(() => {
    if (!target) return undefined;
    if (isLive) {
      const intervalId = setInterval(
        () => dispatch(resyncAsyncOperationsTopic(target)),
        RESYNC_INTERVAL_MS
      );
      return () => clearInterval(intervalId);
    }
    if (!isWaiting) return undefined;
    let timeoutId: ReturnType<typeof setTimeout>;
    let isCancelled = false;
    const poll = async () => {
      // dispatch returns the thunk's promise, so polls never overlap
      await dispatch(loadRecallOperationsSnapshot(target.serviceCenterId));
      if (!isCancelled) timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
    };
    timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [target, isLive, isWaiting]);

  // operations that ended while the page watched: reload the data they changed, and show how the
  // last one ended. A failed one may have saved part of its work, and the makes drop the flags set
  // when submitting.
  useEffect(() => {
    if (!isFollowingRef.current) {
      isFollowingRef.current = true;
      return;
    }
    if (!target || !finishedOperations.length) return;
    const operationIds = finishedOperations.map(operation => operation.operationId);
    dispatch(finishedOperationsHandled(operationIds));
    reloadRecalls(target.serviceCenterId);
    if (finishedOperations.some(operation => operation.subType === 'ServiceCenterMakesSync')) {
      dispatch(loadMakesAll(target.serviceCenterId));
    }
    setFinishedOperation(finishedOperations[finishedOperations.length - 1]);
  }, [finishedOperations]);

  useEffect(() => {
    if (finishedOperation?.status !== 'Completed') return undefined;
    const timeoutId = setTimeout(() => setFinishedOperation(null), COMPLETED_VISIBLE_MS);
    return () => clearTimeout(timeoutId);
  }, [finishedOperation]);

  // the data changed under an operation the page does not show: reload quietly, with no progress
  useEffect(() => {
    if (!target || !dataChangedAt || dataChangedAt === lastDataChangedAtRef.current) return;
    lastDataChangedAtRef.current = dataChangedAt;
    reloadRecalls(target.serviceCenterId);
  }, [dataChangedAt]);

  return {
    /** The page whose operations the banner shows; null while live progress is off */
    target,
    finishedOperation: isOperationRunning ? null : finishedOperation,
    dismissFinished: () => setFinishedOperation(null),
    isOperationRunning: isWaiting,
  };
};
